import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Page, PageHeader, StatusBadge, Empty } from "@/components/shared";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { KYC_STATUS, RISK, fmtDate } from "@/lib/labels";
import { ShieldCheck, RefreshCw, AlertTriangle } from "lucide-react";

function KycTable({ rows, nav }) {
  if (rows.length === 0) return <Empty icon={ShieldCheck} title="Ingen KYC-saker" hint="Send en KYC-invitasjon fra et kundekort." />;
  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden mt-4">
      <table className="w-full text-sm">
        <thead><tr className="border-b border-border text-left">{["Kunde", "Org.nr", "KYC-status", "Risiko", "Neste kontroll"].map((h) => <th key={h} className="px-4 py-3 label-caps">{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id} data-testid={`kyc-row-${c.id}`} onClick={() => nav(`/kunder/${c.id}`)} className="border-b border-border/50 hover:bg-accent/50 cursor-pointer">
              <td className="px-4 py-3 font-medium">{c.legal_name}</td>
              <td className="px-4 py-3 text-muted-foreground">{c.org_number}</td>
              <td className="px-4 py-3"><StatusBadge map={KYC_STATUS} value={c.kyc_status} /></td>
              <td className="px-4 py-3">{c.risk_level ? <StatusBadge map={RISK} value={c.risk_level} /> : "—"}</td>
              <td className="px-4 py-3 text-muted-foreground text-xs">{c.next_control ? fmtDate(c.next_control) : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function KYC() {
  const [rows, setRows] = useState([]);
  const [renewals, setRenewals] = useState([]);
  const nav = useNavigate();
  useEffect(() => {
    api.get("/customers").then((r) => setRows(r.data.filter((c) => c.kyc_status && c.kyc_status !== "ikke_pabegynt")));
    api.get("/kyc/renewals").then((r) => setRenewals(r.data));
  }, []);

  return (
    <Page>
      <PageHeader title="KYC & AML" subtitle="Kundekontroll, eierskap, risikostatus og løpende oppfølging" testid="kyc-header" />
      <Tabs defaultValue="saker">
        <TabsList className="bg-secondary">
          <TabsTrigger value="saker" data-testid="tab-kyc-saker">KYC-saker</TabsTrigger>
          <TabsTrigger value="fornyelse" data-testid="tab-kyc-fornyelse">
            Ny kontroll kreves {renewals.length > 0 && <span className="ml-1.5 text-xs bg-amber-500/20 text-amber-400 rounded-full px-1.5">{renewals.length}</span>}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="saker"><KycTable rows={rows} nav={nav} /></TabsContent>
        <TabsContent value="fornyelse">
          {renewals.length === 0 ? <Empty icon={RefreshCw} title="Ingen kontroller forfaller" hint="KYC-fornyelse varsles automatisk 30 dager før neste kontroll." /> : (
            <div className="bg-card border border-border rounded-xl overflow-hidden mt-4">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border text-left">{["Kunde", "Risiko", "Sist kontrollert", "Neste kontroll", "Status"].map((h) => <th key={h} className="px-4 py-3 label-caps">{h}</th>)}</tr></thead>
                <tbody>
                  {renewals.map((c) => (
                    <tr key={c.id} data-testid={`renewal-row-${c.id}`} onClick={() => nav(`/kunder/${c.id}`)} className="border-b border-border/50 hover:bg-accent/50 cursor-pointer">
                      <td className="px-4 py-3 font-medium">{c.legal_name}</td>
                      <td className="px-4 py-3">{c.risk_level ? <StatusBadge map={RISK} value={c.risk_level} /> : "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground text-xs">{fmtDate(c.last_control)}</td>
                      <td className="px-4 py-3 text-xs">{fmtDate(c.next_control)}</td>
                      <td className="px-4 py-3">{c.overdue ? <span className="inline-flex items-center gap-1 text-xs text-red-400"><AlertTriangle className="w-3 h-3" /> Forfalt</span> : <span className="text-xs text-amber-400">Forfaller snart</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </Page>
  );
}
